<template>
  <div class="doi-upload">
    <form @submit.prevent="searchPaperBeforeSubmitMethod">
      <!-- Search Input Section -->
      <div class="doi-upload__form-group">
        <label for="doi-search-input" class="doi-upload__label">Enter DOI</label>
        <div class="doi-upload__search-wrapper">
          <input
            id="doi-search-input"
            v-model.trim="searchTerm"
            v-validate="{ required: true, url: { require_protocol: false } }"
            type="text"
            name="search_term"
            data-vv-as="search doi"
            autocomplete="off"
            placeholder="e.g. 10.1000/182 or https://doi.org/..."
            class="doi-upload__input"
            :class="{ 'doi-upload__input--invalid': errors.has('search_term') }"
          />
          <button
            type="submit"
            class="doi-upload__search-btn"
            :class="{ 'doi-upload__search-btn--active': searchTerm.length > 0 }"
            :disabled="loader"
            aria-label="Search DOI"
          >
            <i class="fa fa-search" aria-hidden="true"></i>
          </button>
        </div>
        <small v-if="errors.has('search_term')" class="doi-upload__error">
          {{ errors.first('search_term') }}
        </small>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loader" class="doi-upload__spinner-wrapper">
        <div class="doi-upload__spinner"></div>
      </div>

      <!-- Search Result Card -->
      <div v-if="searchResultPapers" class="doi-upload__results">
        <article class="doi-upload__card">
          <h2 class="doi-upload__card-title">
            <a
              v-if="searchResultPapers.url"
              :href="searchResultPapers.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ searchResultPapers.title || 'Untitled Paper' }}
            </a>
            <span v-else>{{ searchResultPapers.title || 'Untitled Paper' }}</span>
          </h2>

          <p v-if="searchResultPapers.author" class="doi-upload__card-author">
            {{ searchResultPapers.author }}
          </p>

          <p
            v-if="searchResultPapers.month || searchResultPapers.year"
            class="doi-upload__card-date"
          >
            {{ searchResultPapers.month }}
            <template v-if="searchResultPapers.month && searchResultPapers.year"> - </template>
            {{ searchResultPapers.year }}
          </p>

          <button
            type="button"
            class="doi-upload__btn-save"
            @click="updatePpDetailMethod(searchResultPapers)"
          >
            <i class="fa fa-bookmark-o" aria-hidden="true"></i>
            <span>Save</span>
          </button>
        </article>
      </div>
    </form>

    <!-- Save / Update Paper Details Modal -->
    <update-pp-details
      v-if="savePaperPopupState"
      :paper-info="paperUpdateInfo"
    ></update-pp-details>
  </div>
</template>

<script>
import axios from 'axios';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';
import UpdatePpDetails from '../../save-doi-paper-popup';

const paperUploadFromDoi = root.uploadPaperFromDoiApiUrl.link;

export default {
  name: 'AddPaperFromDoi',
  components: {
    'update-pp-details': UpdatePpDetails
  },
  data() {
    return {
      searchTerm: '',
      savePaperPopupState: false,
      paperUpdateInfo: null,
      searchResultPapers: null,
      loader: false
    };
  },
  mounted() {
    bus.$on('savePaperPopupStateBus', (data) => {
      this.savePaperPopupState = data;
    });
  },
  beforeDestroy() {
    bus.$off('savePaperPopupStateBus');
  },
  methods: {
    updatePpDetailMethod(data) {
      this.paperUpdateInfo = data;
      this.savePaperPopupState = true;
      document.body.classList.add('body_right_scroll_remove');
    },

    async searchPaperBeforeSubmitMethod() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.searchResultPapers = null;
      this.loader = true;

      try {
        const res = await axios.get(paperUploadFromDoi, {
          params: { doi: this.searchTerm }
        });

        if (res.data && res.data.data && res.data.data.result) {
          this.searchResultPapers = res.data.data.result;
        } else {
          toast.error('No paper details found for this DOI');
        }
      } catch (err) {
        const errorMsg =
          (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
          (err.data && (err.data.user_msg || err.data.msg)) ||
          'Failed to retrieve DOI details';
        toast.error(errorMsg);
      } finally {
        this.loader = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$theme-gold: #ffd400;
$theme-gold-light: #fffbb4;
$accent-blue: #87cefa;
$border-translucent: rgba(255, 255, 255, 0.1);
$bg-translucent: rgba(255, 255, 255, 0.03);
$error-color: #dc3545;
$text-light: #ffffff;
$text-muted: #d6d6d6;

.doi-upload {
  letter-spacing: 0.5px;
  width: 100%;

  &__form-group {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 1rem;
    color: $text-light;
    margin-bottom: 8px;
    font-weight: 500;
  }

  &__search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 10px 48px 10px 16px;
    font-size: 1rem;
    color: $theme-gold;
    background: $bg-translucent;
    border: 1px solid $border-translucent;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      border-color: $theme-gold;

      & ~ .doi-upload__search-btn {
        color: $theme-gold;
      }
    }

    &--invalid {
      border-color: $error-color !important;
    }
  }

  &__search-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    font-size: 1.1rem;
    cursor: pointer;
    padding: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease, transform 0.15s ease;

    &--active {
      color: $theme-gold;

      &:hover {
        color: $accent-blue;
        transform: translateY(-50%) scale(1.1);
      }
    }

    &:focus-visible {
      outline: 2px solid $theme-gold;
      border-radius: 4px;
    }
  }

  &__error {
    color: $error-color;
    font-size: 0.875rem;
    margin-top: 6px;
  }

  // Loading Spinner
  &__spinner-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 0;
  }

  &__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: $theme-gold;
    border-radius: 50%;
    animation: doi-spin 0.8s linear infinite;
  }

  // Result Card
  &__results {
    margin-top: 20px;
  }

  &__card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__card-title {
    font-size: 1.15rem;
    line-height: 1.4;
    margin: 0 0 8px;
    font-weight: 600;

    a {
      color: $accent-blue;
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: $theme-gold;
        text-decoration: underline;
      }
    }
  }

  &__card-author {
    font-size: 0.95rem;
    font-family: 'Georgia', serif;
    color: $text-muted;
    line-height: 1.4;
    margin: 0 0 6px;
  }

  &__card-date {
    font-size: 0.9rem;
    color: $theme-gold-light;
    margin: 0 0 16px;
    text-transform: capitalize;
  }

  &__btn-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: rgba(255, 255, 255, 0.1);
    color: $theme-gold;
    border: 1px solid rgba(255, 212, 0, 0.3);
    border-radius: 4px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $accent-blue;
      border-color: $accent-blue;
      color: #1a1a1a;
    }
  }
}

@keyframes doi-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>